import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { IconButton } from './IconButton.js'

// ActionMenu — kebab trigger + popover with icon + label rows.
//
// The popover is rendered into `document.body` via React's createPortal and
// positioned in viewport coordinates (`position: fixed`). This means it
// escapes any ancestor `overflow:hidden` / `overflow:auto` chain, so the
// menu never gets clipped by its host panel and never introduces a
// scrollbar inside that panel.

export interface ActionMenuItem {
  key: string
  label: string
  icon: ReactNode
  onClick: () => void
  /** Optional visual hint: 'danger' colours the row red. */
  tone?: 'default' | 'danger' | 'accent'
  disabled?: boolean
}

export interface ActionMenuProps {
  items: ActionMenuItem[]
  /** ARIA label for the trigger. */
  label: string
  /** Optional test id forwarded to the trigger button. */
  testid?: string
  /** Alignment of the dropdown panel relative to the trigger. */
  align?: 'left' | 'right'
  /** Extra classes forwarded to the trigger IconButton. */
  triggerClassName?: string
}

export function ActionMenu({
  items,
  label,
  testid,
  align = 'right',
  triggerClassName,
}: ActionMenuProps) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)

  function recalc() {
    if (!triggerRef.current) return
    setAnchor(triggerRef.current.getBoundingClientRect())
  }

  useLayoutEffect(() => {
    if (!open) return
    recalc()
    const onScroll = () => recalc()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onPointer(e: MouseEvent) {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const panelStyle: React.CSSProperties = anchor
    ? align === 'right'
      ? { position: 'fixed', top: anchor.bottom + 4, right: window.innerWidth - anchor.right }
      : { position: 'fixed', top: anchor.bottom + 4, left: anchor.left }
    : { position: 'fixed', top: -9999, left: -9999 }

  return (
    <>
      <IconButton
        ref={triggerRef}
        label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        className={triggerClassName}
      >
        <IconKebab />
      </IconButton>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            style={panelStyle}
            className="z-50 min-w-[160px] overflow-hidden rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] py-1 shadow-[var(--xr-shadow-md)]"
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return
                  setOpen(false)
                  item.onClick()
                }}
                className={
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-50 ' +
                  (item.tone === 'danger'
                    ? 'text-[var(--xr-danger)] hover:bg-[var(--xr-danger-soft)]'
                    : item.tone === 'accent'
                      ? 'text-[var(--xr-accent)] hover:bg-[var(--xr-accent-soft)]'
                      : 'text-[var(--xr-text-primary)] hover:bg-[var(--xr-bg-surface-elev)]')
                }
                data-testid={`action-menu-${item.key}`}
              >
                <span className="flex h-4 w-4 flex-none items-center justify-center">
                  {item.icon}
                </span>
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  )
}

function IconKebab() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" stroke="none" aria-hidden>
      <circle cx="12" cy="5" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="19" r="1.5" />
    </svg>
  )
}
